import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWeb3 } from '../contexts/Web3Context';
import { useQuery, useQueryClient } from 'react-query';
import logo from '../assets/worklob-logo-cp-no-bg.png';
import { useSidebar } from '../contexts/SidebarContext';
import axios from 'axios';
import {
  LayoutDashboard,
  Search,
  Briefcase,
  Star,
  MessageSquare,
  Wallet,
  Users,
  Settings,
  Shield,
  Gift,
  Clock,
  FolderArchive,
  Calendar,
  DollarSign,
  KeyRound,
  Grid,
  Lock,
  BookOpen
} from 'lucide-react';
import { DEPLOYER_WALLET_ADDRESS } from '../config/Constants';
import API_URL from '../config/Backendurl';

const SidebarWidget = () => {
  const { user, isAuthenticated, switchRole } = useAuth();
  const { isConnected, walletBalance } = useWeb3();
  const { isSidebarOpen, closeSidebar } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [expandedSections, setExpandedSections] = useState({});
  const [isRoleSwitching, setIsRoleSwitching] = useState(false);
  const [usdcPrice, setUsdcPrice] = useState(null);

  // Fetch USDC price for USD conversion (treated ~1:1)
  const { data: usdcPriceData } = useQuery(
    'usdcPrice',
    async () => {
      try {
        const response = await axios.get(`${API_URL}/api/v1/price/usdc`);
        return response.data;
      } catch (error) {
        console.error("Failed to get USDC price from backend:", error);
        return null;
      }
    },
    {
      select: (data) => data,
      staleTime: 300000, // Cache for 5 minutes
      refetchInterval: 300000, // Refetch every 5 minutes
      onSuccess: (data) => {
        if (data?.usd) {
          setUsdcPrice(data.usd);
        }
      }
    }
  );

  // Update usdcPrice state when data changes
  useEffect(() => {
    if (usdcPriceData?.usd) {
      setUsdcPrice(usdcPriceData.usd);
    }
  }, [usdcPriceData]);

  // Calculate available balance in USD
  const availableBalanceUSD = useMemo(() => {
    if (!isConnected) return 0;
    const balanceUSDC = parseFloat(walletBalance) || 0;
    if (isNaN(balanceUSDC)) return 0;
    const price = usdcPrice ?? 1;
    return balanceUSDC * price;
  }, [isConnected, walletBalance, usdcPrice]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const isActive = (path) => location.pathname === path;

  const handleRoleSwitch = async () => {
    setIsRoleSwitching(true);
    try {
      await switchRole(navigate, queryClient);
    } catch (error) {
      console.error('Role switch failed:', error);
    } finally {
      setIsRoleSwitching(false);
    }
  };

  // Common menu items for all users
  const commonMenuItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/wallet', icon: Wallet, label: 'Wallet' }
  ];

  // Talent-specific menu items
  const talentMenuItems = [
    ...commonMenuItems,
    { 
      path: '/jobs', 
      icon: Search, 
      label: 'Browse Jobs',
      type: 'jobs'
    },
   
    { 
      section: 'My Work',
      items: [
        { path: '/dashboard?tab=gigs', icon: Star, label: 'My Gigs' },
        { path: '/dashboard?tab=freelance-jobs', icon: Clock, label: 'My Freelance Jobs' },
        { path: '/dashboard?tab=full-time-jobs', icon: Calendar, label: 'My Full-time Jobs' },
        { path: '/dashboard?tab=others', icon: Grid, label: 'Others' },
      ]
    },
    { path: '/staking', icon: Lock, label: 'Staking' },
    { path: '/referral', icon: Gift, label: 'Referral' },
    { path: '/governance', icon: Shield, label: 'Governance' },
    { path: '/blogger', icon: BookOpen, label: 'Blogs' },
    { path: '/settings', icon: Settings, label: 'Settings' }
  ];

  // Client-specific menu items
  const clientMenuItems = [
    ...commonMenuItems,
    { 
      path: '/gigs', 
      icon: Search, 
      label: 'Browse Gigs',
      type: 'gigs'
    },
    { 
      section: 'My Work',
      items: [
        { path: '/dashboard?tab=gigs', icon: Star, label: 'My Gigs' },
        { path: '/dashboard?tab=freelance-jobs', icon: Clock, label: 'My Freelance Jobs' },
        { path: '/dashboard?tab=full-time-jobs', icon: Calendar, label: 'My Full-time Jobs' },
        { path: '/dashboard?tab=others', icon: Grid, label: 'Others' },
      ]
    },
    { path: '/staking', icon: Lock, label: 'Staking' },
    { path: '/referral', icon: Gift, label: 'Referral' },
    { path: '/governance', icon: Shield, label: 'Governance' },
    { path: '/blogger', icon: BookOpen, label: 'Blogs' },
    { path: '/settings', icon: Settings, label: 'Settings' }
  ];

  const isDeployerWallet = useMemo(() => {
    if (!DEPLOYER_WALLET_ADDRESS || !user?.walletAddress) return false;
    return (
      user.walletAddress.toLowerCase() === DEPLOYER_WALLET_ADDRESS.toLowerCase()
    );
  }, [user?.walletAddress]);

  const baseMenu = user?.role === 'talent' ? talentMenuItems : clientMenuItems;
  const menuItems = useMemo(() => {
    const items = [...baseMenu];
    if (isDeployerWallet) {
      items.push({
        path: '/deployer',
        icon: KeyRound,
        label: 'Deployer Console'
      });
    }
    return items;
  }, [baseMenu, isDeployerWallet]);

  // If user is not authenticated, show a simplified sidebar
  if (!isAuthenticated) {
    return (
      <>
        {/* Mobile Overlay */}
        {isSidebarOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden" 
            onClick={closeSidebar}
          />
        )}
        
        <aside className={`fixed left-0 top-0 bottom-0 w-64 bg-surface border-r border-gray-200 dark:border-gray-700 transition-all duration-300 z-40 ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}>
        <div className="p-4">
          {/* App Title */}
          <div className="flex items-center space-x-3 mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
  <div className="w-10 h-10 rounded-lg overflow-hidden flex items-center justify-center">
    <img
      src={logo} // Replace with your actual logo path
      alt="Workloob Logo"
      className="w-full h-full object-contain"
    />
  </div>
  <div>
    <h1 className="text-xl font-bold text-heading dark:text-white">
      Workloob
    </h1>
    <p className="text-xs text-gray-600 dark:text-gray-400">
      Freelance Marketplace
    </p>
  </div>
</div>

          <div className="space-y-4">
            <div className="text-center py-8">
              <div className="text-4xl mb-4">🚀</div>
              <h3 className="text-lg font-heading text-heading mb-2">Welcome to Workloob</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Sign in to access your dashboard and features
              </p>
            </div>
            
            <div className="space-y-2">
              <Link
                to="/jobs"
                className={`flex items-center px-4 py-2 rounded-lg transition-colors duration-200 ${
                  isActive('/jobs') 
                    ? 'bg-accent text-white' 
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <Search className="w-5 h-5 mr-3" />
                Browse Jobs
              </Link>
              
              <Link
                to="/gigs"
                className={`flex items-center px-4 py-2 rounded-lg transition-colors duration-200 ${
                  isActive('/gigs') 
                    ? 'bg-accent text-white' 
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <Star className="w-5 h-5 mr-3" />
                Browse Gigs
              </Link>
            </div>
          </div>
        </div>
      </aside>
      </>
    );
  }

  return (
    <>
      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden" 
          onClick={closeSidebar}
        />
      )}
      
      <aside className={`fixed left-0 top-0 bottom-0 w-64 bg-surface border-r border-gray-200 dark:border-gray-700 transition-all duration-300 z-40 overflow-y-auto ${
        isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      }`}>
      <div className="p-4">
        {/* App Title */}
        <div className="flex items-center space-x-3 mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
  <div className="w-10 h-10 rounded-lg overflow-hidden flex items-center justify-center">
    <img
      src={logo} // Replace with your actual logo path
      alt="Workloob Logo"
      className="w-full h-full object-contain"
    />
  </div>
  <div>
    <h1 className="text-xl font-bold text-heading dark:text-white">
      Workloob
    </h1>
    <p className="text-xs text-gray-600 dark:text-gray-400">
      Freelance Marketplace
    </p>
  </div>
</div>

        {/* User Info */}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6">
          <div className="flex items-center space-x-3">
            {user?.profile?.avatar ? (
              <img
                src={user.profile.avatar}
                alt={user?.username || 'User'}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 bg-accent rounded-full flex items-center justify-center text-white font-medium">
                {user?.profile?.firstName?.[0] || user?.username?.[0]?.toUpperCase() || 'U'}
              </div>
            )}
            <div>
              <div className="font-medium text-gray-900 dark:text-gray-100">
                {user?.profile?.firstName} {user?.profile?.lastName}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400 capitalize">
                {user?.role}
              </div>
            </div>
          </div>
          
          {/* Role Switch Button */}
          <div className="mt-3">
            <button
              onClick={handleRoleSwitch}
              disabled={isRoleSwitching}
              className="w-full flex items-center justify-center space-x-2 px-3 py-2 bg-accent/10 text-accent rounded-lg hover:bg-accent/20 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Users className="w-4 h-4" />
              <span className="text-sm font-medium">
                {isRoleSwitching ? 'Switching...' : `Switch to ${user?.role === 'talent' ? 'Client' : 'Talent'}`}
              </span>
            </button>
          </div>
          
          {/* Activity Points */}
          <div className="mt-3 flex items-center justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">Activity Points</span>
            <span className="font-medium text-accent">{user?.stats?.activityPoints || 0}</span>
          </div>
          
          {/* Available Balance (from connected wallet) */}
          <div className="mt-2 flex items-center justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">Available Balance</span>
            <div className="flex items-center space-x-1">
              <DollarSign className="w-3 h-3 text-green-500" />
              <span className="font-medium text-green-600 dark:text-green-400">
                ${availableBalanceUSD.toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="space-y-2">
          {menuItems.map((item, index) => {
            if (item.section) {
              return (
                <div key={index}>
                  <button
                    onClick={() => toggleSection(item.section)}
                    className="flex items-center w-full px-4 py-2 text-left text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors duration-200 font-medium"
                  >
                    <span className="mr-3">📋</span>
                    {item.section}
                    <span className={`ml-auto transition-transform duration-200 ${expandedSections[item.section] ? 'rotate-180' : ''}`}>
                      ▼
                    </span>
                  </button>
                  
                  {expandedSections[item.section] && (
                    <div className="ml-4 mt-2 space-y-1">
                      {item.items.map((subItem, subIndex) => (
                        <Link
                          key={subIndex}
                          to={subItem.path}
                          className={`flex items-center px-4 py-2 rounded-lg transition-colors duration-200 ${
                            isActive(subItem.path.replace('?tab=', '')) 
                              ? 'bg-accent text-white' 
                              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                          }`}
                        >
                          <subItem.icon className="w-4 h-4 mr-3" />
                          {subItem.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <Link
                key={index}
                to={item.path}
                className={`flex items-center px-4 py-2 rounded-lg transition-colors duration-200 ${
                  isActive(item.path.split('?')[0]) 
                    ? 'bg-accent text-white' 
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <item.icon className="w-5 h-5 mr-3" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* Quick Actions */}
        <div className="mt-8 space-y-2">
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">
            Quick Actions
          </div>
          
          <div className="space-y-2">
            {user?.role === 'talent' ? (
              <>
                <Link
                  to="/create-gig"
                  className="flex items-center px-4 py-2 bg-gradient-to-r from-accent to-blue-600 text-white rounded-lg hover:from-accent/90 hover:to-blue-600/90 transition-all duration-200"
                >
                  <Star className="w-4 h-4 mr-2" />
                  Create Gig
                </Link>
                <Link
                  to="/jobs"
                  className="flex items-center px-4 py-2 border border-accent text-accent rounded-lg hover:bg-accent hover:text-white transition-all duration-200"
                >
                  <Search className="w-4 h-4 mr-2" />
                  Find Jobs
                </Link>
              </>
            ) : (
              <>
                <Link
                  to="/create-job"
                  className="flex items-center px-4 py-2 bg-gradient-to-r from-accent to-blue-600 text-white rounded-lg hover:from-accent/90 hover:to-blue-600/90 transition-all duration-200"
                >
                  <Briefcase className="w-4 h-4 mr-2" />
                  Post Job
                </Link>
                <Link
                  to="/gigs"
                  className="flex items-center px-4 py-2 border border-accent text-accent rounded-lg hover:bg-accent hover:text-white transition-all duration-200"
                >
                  <Search className="w-4 h-4 mr-2" />
                  Find Gigs
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </aside>
    </>
  );
};

export default SidebarWidget;
